import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Star, Quote, ChevronLeft, ChevronRight, MessageSquareHeart, Sparkles, PlusCircle } from 'lucide-react';
import api from '../../config/api';

const DEFAULT_TESTIMONIALS = [
  {
    id: 'starter-1',
    name: 'Priya Sharma',
    role: 'Event Attendee',
    rating: 5,
    comment: 'The energy at Beats of Washington events is truly electrifying! It brings our diverse community together through the universal power of music and cultural celebrations.',
    createdAt: '2026-08-15T12:00:00Z'
  },
  {
    id: 'starter-2',
    name: 'David Miller',
    role: 'Community Volunteer',
    rating: 5,
    comment: 'Volunteering with BOW has been an inspiring experience. The team’s dedication to youth music education and community outreach is second to none.',
    createdAt: '2026-08-20T12:00:00Z'
  },
  {
    id: 'starter-3',
    name: 'Ananya Patel',
    role: 'Youth Music Program Parent',
    rating: 5,
    comment: 'My kids loved every moment of the workshops. They not only learned traditional rhythms but also built lifelong friendships. Thank you Beats of Washington!',
    createdAt: '2026-09-01T12:00:00Z'
  }
];

export default function FeedbackCarousel({ onOpenFeedbackModal }) {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isFading, setIsFading] = useState(false);
  const timerRef = useRef(null);

  // Fetch approved feedbacks
  const fetchFeedbacks = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/feedback');
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) {
          setFeedbacks(data);
          return;
        }
      }
      setFeedbacks(DEFAULT_TESTIMONIALS);
    } catch (err) {
      console.warn('Could not fetch feedbacks, using defaults:', err);
      setFeedbacks(DEFAULT_TESTIMONIALS);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFeedbacks();
  }, [fetchFeedbacks]);

  // Handle slide advance with fade transition
  const changeSlide = useCallback((newIndex) => {
    setIsFading(true);
    setTimeout(() => {
      setCurrentIndex(newIndex);
      setIsFading(false);
    }, 250);
  }, []);

  const handleNext = useCallback(() => {
    if (feedbacks.length <= 1) return;
    const next = (currentIndex + 1) % feedbacks.length;
    changeSlide(next);
  }, [currentIndex, feedbacks.length, changeSlide]);

  const handlePrev = useCallback(() => {
    if (feedbacks.length <= 1) return;
    const prev = (currentIndex - 1 + feedbacks.length) % feedbacks.length;
    changeSlide(prev);
  }, [currentIndex, feedbacks.length, changeSlide]);

  // 3-second auto-advancing slideshow (pauses on hover)
  useEffect(() => {
    if (isPaused || feedbacks.length <= 1) return;

    timerRef.current = setInterval(() => {
      handleNext();
    }, 3000); // 3 seconds as requested

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isPaused, feedbacks.length, handleNext]);

  const currentItem = feedbacks[currentIndex] || DEFAULT_TESTIMONIALS[0];

  // Helper to generate initials from name
  const getInitials = (name) => {
    if (!name || name === 'Anonymous') return 'BW';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  return (
    <section 
      className="relative bg-gradient-to-b from-amber-50/50 via-white to-orange-50/30 py-8 sm:py-12 border-b border-orange-100/60 overflow-hidden"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={() => setIsPaused(true)}
      onTouchEnd={() => setIsPaused(false)}
    >
      {/* Decorative ambient background blurbs */}
      <div className="absolute top-0 left-1/4 w-72 h-72 bg-gradient-to-br from-amber-200/20 to-orange-200/10 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-gradient-to-br from-red-200/15 to-yellow-200/20 rounded-full blur-3xl pointer-events-none"></div>

      <div className="container-custom px-4 sm:px-6 relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Section Header Bar */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mb-6 sm:mb-8 text-center sm:text-left">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100/80 text-amber-800 text-xs font-bold uppercase tracking-wider mb-2 border border-amber-200/60">
                <Sparkles className="w-3.5 h-3.5 text-amber-600" />
                Community Feedback & Reviews
              </div>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-extrabold text-gray-900 flex items-center justify-center sm:justify-start gap-2">
                What People Say About BOW
              </h2>
            </div>

            {/* Leave Feedback Action Button */}
            <button
              onClick={onOpenFeedbackModal}
              className="inline-flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 rounded-full bg-gradient-to-r from-orange-500 via-amber-500 to-red-500 text-white font-bold text-xs sm:text-sm shadow-md hover:shadow-lg hover:from-orange-600 hover:to-red-600 transition-all duration-300 hover:scale-105 group"
            >
              <PlusCircle className="w-4 h-4 transition-transform group-hover:rotate-90" />
              <span>Leave Feedback</span>
            </button>
          </div>

          {/* Testimonial Card Display */}
          <div className="relative bg-white/90 backdrop-blur-md rounded-3xl p-6 sm:p-8 md:p-10 shadow-xl border border-orange-100/80 overflow-hidden min-h-[220px] sm:min-h-[200px] flex flex-col justify-between group transition-all duration-300">
            {/* Top decorative quote accent */}
            <Quote className="absolute top-4 right-6 w-16 h-16 sm:w-20 sm:h-20 text-orange-200/30 pointer-events-none" />

            {/* Animated Content */}
            <div className={`transition-opacity duration-300 ${isFading ? 'opacity-0 scale-[0.99]' : 'opacity-100 scale-100'}`}>
              {/* Star Rating */}
              <div className="flex items-center gap-1 mb-3">
                {[1, 2, 3, 4, 5].map((star) => {
                  const isFilled = star <= (currentItem.rating || 5);
                  return (
                    <Star
                      key={star}
                      className={`w-5 h-5 sm:w-6 sm:h-6 ${
                        isFilled
                          ? 'text-amber-400 fill-amber-400 drop-shadow-[0_2px_6px_rgba(251,191,36,0.4)]'
                          : 'text-gray-200'
                      }`}
                    />
                  );
                })}
                <span className="ml-2 text-xs font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">
                  {currentItem.rating ? `${currentItem.rating}.0 / 5.0` : '5.0 / 5.0'}
                </span>
              </div>

              {/* Comment text */}
              <p className="text-gray-800 text-base sm:text-lg md:text-xl font-medium leading-relaxed mb-6 italic line-clamp-4">
                "{currentItem.comment}"
              </p>

              {/* Author & Meta */}
              <div className="flex items-center justify-between flex-wrap gap-3 pt-4 border-t border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-gradient-to-tr from-orange-500 via-amber-500 to-red-500 text-white font-bold flex items-center justify-center text-sm shadow-md">
                    {getInitials(currentItem.name)}
                  </div>
                  <div>
                    <h4 className="text-sm sm:text-base font-bold text-gray-900">
                      {currentItem.name || 'Community Member'}
                    </h4>
                    <span className="inline-block text-xs font-medium text-orange-600">
                      {currentItem.role || 'Community Supporter'}
                    </span>
                  </div>
                </div>

                {/* Next / Prev Controls */}
                {feedbacks.length > 1 && (
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <button
                      onClick={handlePrev}
                      className="p-2 rounded-full bg-gray-100 hover:bg-orange-100 text-gray-700 hover:text-orange-600 transition-colors duration-200 focus:outline-none"
                      aria-label="Previous testimonial"
                    >
                      <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                    <button
                      onClick={handleNext}
                      className="p-2 rounded-full bg-gray-100 hover:bg-orange-100 text-gray-700 hover:text-orange-600 transition-colors duration-200 focus:outline-none"
                      aria-label="Next testimonial"
                    >
                      <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Pagination Dots (bottom indicators) */}
            {feedbacks.length > 1 && (
              <div className="flex items-center justify-center gap-1.5 mt-5">
                {feedbacks.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => changeSlide(idx)}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      idx === currentIndex
                        ? 'w-6 bg-gradient-to-r from-orange-500 to-amber-500'
                        : 'w-2 bg-gray-200 hover:bg-gray-300'
                    }`}
                    aria-label={`Go to slide ${idx + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
